import { onWorkspacePaused } from "@weco/suite-client";
import { markWorkspacePaused } from "./workspacePausedNotice";

export interface WorkspacePausedHandlerDeps {
  isAuthenticated: () => boolean;
  /** The shell store's single-flight refresh. */
  refresh: () => Promise<boolean>;
  /** Re-read billing status (the billing-only gate also reads it). */
  invalidateStatus: () => void;
  now?: () => number;
}

/** After handling one, ignore further reports for this long. */
export const WORKSPACE_PAUSED_COOLDOWN_MS = 30_000;

/**
 * What the shell does when any API answers `403 workspace_paused`.
 *
 * It does not guess who the user is. It renews the session, and People
 * decides: a member is refused (`403 workspace_paused` on refresh), so the
 * refresh fails, auth is cleared and LoginScreen explains the pause; an Owner
 * or Admin gets a new token stamped `org_access: "billing_only"`, and the
 * shell swaps itself for the billing-only screen.
 *
 * One handling at a time, then a cooldown: a module firing ten requests that
 * all come back paused must cause one renewal, not ten.
 */
export function createWorkspacePausedHandler(deps: WorkspacePausedHandlerDeps): () => Promise<void> {
  const now = deps.now ?? (() => Date.now());
  let inFlight: Promise<void> | null = null;
  let lastHandledAt = -Infinity;

  return () => {
    if (inFlight) return inFlight;
    if (now() - lastHandledAt < WORKSPACE_PAUSED_COOLDOWN_MS) return Promise.resolve();
    if (!deps.isAuthenticated()) return Promise.resolve();
    inFlight = (async () => {
      try {
        const ok = await deps.refresh();
        // The trigger was a paused answer, so a refused renewal means the
        // pause — whatever status the refresh itself came back with.
        if (!ok) markWorkspacePaused();
        else deps.invalidateStatus();
      } finally {
        lastHandledAt = now();
        inFlight = null;
      }
    })();
    return inFlight;
  };
}

/** Listen for paused answers from every API client. Returns the unsubscribe. */
export function installWorkspacePausedHandler(deps: WorkspacePausedHandlerDeps): () => void {
  const handle = createWorkspacePausedHandler(deps);
  return onWorkspacePaused(() => {
    void handle();
  });
}
